import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { RecurringItem, Class } from '@homework-app/shared';
import { getApiBaseUrl } from '../constants';

interface UserProfile {
  name: string;
  grade: number | null;
  school: string | null;
}

interface SettingsScreenProps {
  recurringHomework: RecurringItem[];
  classes: Class[];
  onBack: () => void;
  onUpdateRecurring: (recurring: RecurringItem[]) => void;
  onUpdateClasses: (classes: Class[]) => void;
}

const SettingsScreen: React.FC<SettingsScreenProps> = ({
  recurringHomework,
  classes,
  onBack,
  onUpdateRecurring,
  onUpdateClasses
}) => {
  const { getToken } = useAuth();

  // Profile state
  const [profile, setProfile] = useState<UserProfile>({ name: '', grade: null, school: null });
  const [draftProfile, setDraftProfile] = useState<UserProfile>({ name: '', grade: null, school: null });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Local draft state for editing
  const [draftRecurring, setDraftRecurring] = useState<RecurringItem[]>(recurringHomework);
  const [draftClasses, setDraftClasses] = useState<Class[]>(classes);

  // Editing mode state
  const [isEditingClasses, setIsEditingClasses] = useState(false);
  const [isEditingRecurring, setIsEditingRecurring] = useState(false);

  // Saving state
  const [isSavingClasses, setIsSavingClasses] = useState(false);
  const [isSavingRecurring, setIsSavingRecurring] = useState(false);

  // Sync draft state when props change (after save)
  useEffect(() => {
    if (!isEditingRecurring) {
      setDraftRecurring(recurringHomework);
    }
  }, [recurringHomework, isEditingRecurring]);

  useEffect(() => {
    if (!isEditingClasses) {
      setDraftClasses(classes);
    }
  }, [classes, isEditingClasses]);

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${getApiBaseUrl()}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const p = { name: data.name || '', grade: data.grade, school: data.school || '' };
          setProfile(p);
          setDraftProfile(p);
          setProfileLoaded(true);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };
    fetchProfile();
  }, [getToken]);

  const saveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(draftProfile),
      });
      if (res.ok) {
        setProfile(draftProfile);
        setIsEditingProfile(false);
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Check for unsaved changes
  const hasUnsavedClassesChanges = isEditingClasses &&
    JSON.stringify(draftClasses) !== JSON.stringify(classes);
  const hasUnsavedRecurringChanges = isEditingRecurring &&
    JSON.stringify(draftRecurring) !== JSON.stringify(recurringHomework);
  const hasUnsavedChanges = hasUnsavedClassesChanges || hasUnsavedRecurringChanges;

  // Handle back button with unsaved changes warning
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
      );
      if (!confirmed) return;
    }
    onBack();
  }, [hasUnsavedChanges, onBack]);

  // Get only enabled classes for the recurring homework dropdown
  const enabledClasses = draftClasses.filter(c => c.enabled);

  // Classes editing functions
  const toggleClassEnabled = (period: number) => {
    setDraftClasses(prev => prev.map(cls =>
      cls.period === period ? { ...cls, enabled: !cls.enabled } : cls
    ));
  };

  const updateClassSubject = (period: number, subject: string) => {
    setDraftClasses(prev => prev.map(cls =>
      cls.period === period ? { ...cls, subject } : cls
    ));
  };

  const updateClassTeacher = (period: number, teacher: string) => {
    setDraftClasses(prev => prev.map(cls =>
      cls.period === period ? { ...cls, teacher } : cls
    ));
  };

  const startEditingClasses = () => {
    setDraftClasses(classes);
    setIsEditingClasses(true);
  };

  const cancelEditingClasses = () => {
    setDraftClasses(classes);
    setIsEditingClasses(false);
  };

  const saveClasses = async () => {
    setIsSavingClasses(true);
    try {
      await onUpdateClasses(draftClasses);
      setIsEditingClasses(false);
    } catch (error) {
      console.error('Failed to save classes:', error);
    } finally {
      setIsSavingClasses(false);
    }
  };

  // Recurring homework editing functions
  const updateRecurringText = (id: string, text: string) => {
    setDraftRecurring(prev => prev.map(item =>
      item.id === id ? { ...item, text } : item
    ));
  };

  const updateRecurringPeriod = (id: string, period: number) => {
    setDraftRecurring(prev => prev.map(item =>
      item.id === id ? { ...item, period } : item
    ));
  };

  const deleteRecurring = (id: string) => {
    setDraftRecurring(prev => prev.filter(item => item.id !== id));
  };

  const addRecurringItem = () => {
    const defaultPeriod = enabledClasses.length > 0 ? enabledClasses[0].period : 1;
    const newItem: RecurringItem = {
      id: 'rec' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      text: '',
      period: defaultPeriod
    };
    setDraftRecurring(prev => [...prev, newItem]);
  };

  const startEditingRecurring = () => {
    setDraftRecurring(recurringHomework);
    setIsEditingRecurring(true);
  };

  const cancelEditingRecurring = () => {
    setDraftRecurring(recurringHomework);
    setIsEditingRecurring(false);
  };

  const saveRecurring = async () => {
    setIsSavingRecurring(true);
    try {
      await onUpdateRecurring(draftRecurring);
      setIsEditingRecurring(false);
    } catch (error) {
      console.error('Failed to save recurring homework:', error);
    } finally {
      setIsSavingRecurring(false);
    }
  };

  const getClassDisplayName = (period: number) => {
    const cls = draftClasses.find(c => c.period === period);
    return cls ? `${cls.subject} - ${cls.teacher}` : `Period ${period}`;
  };

  // === Connection management state ===
  const [connections, setConnections] = useState<any[]>([]);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [showInviteParent, setShowInviteParent] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');

  const fetchConnections = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/connections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConnections(data.connections || []);
        setPendingInvites(data.pendingInvites || []);
      }
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    }
  };

  useEffect(() => { fetchConnections(); }, []);

  const sendParentInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteMessage('');
    try {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/connections/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: inviteEmail }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send invite');
      }
      setInviteMessage(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInviteParent(false);
      fetchConnections();
    } catch (err: any) {
      setInviteMessage(`Error: ${err.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleConnectionAction = async (id: string, action: 'confirm' | 'reject') => {
    try {
      const token = await getToken();
      await fetch(`${getApiBaseUrl()}/connections/${id}/${action}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchConnections();
    } catch (err) {
      console.error(`Failed to ${action} connection:`, err);
    }
  };

  return (
    <div className="settings-screen">
      <div className="settings-header">
        <button className="back-btn" onClick={handleBack}>
          ← Back
        </button>
        <h1 className="settings-title">Settings</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="settings-content">
        {/* Profile Section */}
        {profileLoaded && (
          <div className="settings-section">
            <div className="section-header">
              <div>
                <h2 className="section-title">Profile</h2>
                <p className="section-description">Your personal information.</p>
              </div>
              <div className="section-actions">
                {!isEditingProfile ? (
                  <button className="edit-btn" onClick={() => { setDraftProfile(profile); setIsEditingProfile(true); }}>
                    Edit
                  </button>
                ) : (
                  <>
                    <button className="cancel-btn" onClick={() => { setDraftProfile(profile); setIsEditingProfile(false); }} disabled={isSavingProfile}>
                      Cancel
                    </button>
                    <button className="save-btn" onClick={saveProfile} disabled={isSavingProfile}>
                      {isSavingProfile ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="profile-fields">
              <div className="profile-field">
                <label className="profile-label">Name</label>
                <input
                  type="text"
                  className="class-input"
                  value={draftProfile.name}
                  onChange={(e) => setDraftProfile(p => ({ ...p, name: e.target.value }))}
                  disabled={!isEditingProfile}
                  readOnly={!isEditingProfile}
                  placeholder="Your name"
                />
              </div>
              <div className="profile-field">
                <label className="profile-label">Grade</label>
                <input
                  type="number"
                  className="class-input"
                  value={draftProfile.grade ?? ''}
                  onChange={(e) => setDraftProfile(p => ({ ...p, grade: e.target.value ? parseInt(e.target.value) : null }))}
                  disabled={!isEditingProfile}
                  readOnly={!isEditingProfile}
                  placeholder="Grade level"
                  min="1"
                  max="12"
                />
              </div>
              <div className="profile-field">
                <label className="profile-label">School</label>
                <input
                  type="text"
                  className="class-input"
                  value={draftProfile.school ?? ''}
                  onChange={(e) => setDraftProfile(p => ({ ...p, school: e.target.value }))}
                  disabled={!isEditingProfile}
                  readOnly={!isEditingProfile}
                  placeholder="School name"
                />
              </div>
            </div>
          </div>
        )}

        {/* Parent Connections Section */}
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2 className="section-title">Parent Connections</h2>
              <p className="section-description">Connect with a parent so they can view your homework.</p>
            </div>
          </div>

          {inviteMessage && (
            <div style={{ padding: '0.6rem 1rem', borderRadius: 8, marginBottom: '1rem', background: inviteMessage.startsWith('Error') ? '#fee2e2' : '#d1fae5', color: inviteMessage.startsWith('Error') ? '#991b1b' : '#065f46', fontSize: '0.9rem' }}>
              {inviteMessage}
            </div>
          )}

          {/* Incoming requests from parents */}
          {pendingInvites.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontWeight: 500, fontSize: '0.9rem', marginBottom: '0.5rem' }}>Incoming Requests</p>
              {pendingInvites.map((inv: any) => (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: '#eff6ff', borderRadius: 8, marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem' }}>{inv.parent_name || inv.invite_email} wants to connect</span>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button onClick={() => handleConnectionAction(inv.id, 'confirm')} style={{ padding: '0.3rem 0.6rem', borderRadius: 6, background: '#4f46e5', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>Accept</button>
                    <button onClick={() => handleConnectionAction(inv.id, 'reject')} style={{ padding: '0.3rem 0.6rem', borderRadius: 6, background: '#e5e7eb', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending outgoing requests to parents */}
          {connections.filter((c: any) => c.status === 'pending' && c.invited_by === 'student').map((conn: any) => (
            <div key={conn.id} style={{ padding: '0.75rem', background: '#fffbeb', borderRadius: 8, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              ⏳ Invitation sent to {conn.parent_email || conn.invite_email} — waiting for confirmation
            </div>
          ))}

          {/* Connected parents */}
          {connections.filter((c: any) => c.status === 'confirmed').map((conn: any) => (
            <div key={conn.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: 8, marginBottom: '0.5rem', fontSize: '0.9rem', color: '#065f46' }}>
              ✅ Connected with {conn.parent_name || conn.parent_email || conn.invite_email}
            </div>
          ))}

          {/* Invite parent button */}
          {!showInviteParent ? (
            <button
              onClick={() => setShowInviteParent(true)}
              className="add-recurring-btn"
              style={{ marginTop: '0.75rem' }}
            >
              + Invite a Parent
            </button>
          ) : (
            <div style={{ marginTop: '0.75rem', padding: '1rem', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="Parent's email address"
                style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box', marginBottom: '0.75rem' }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => { setShowInviteParent(false); setInviteEmail(''); }} style={{ flex: 1, padding: '0.5rem', borderRadius: 6, background: '#e5e7eb', border: 'none', cursor: 'pointer' }}>Cancel</button>
                <button onClick={sendParentInvite} disabled={!inviteEmail.trim() || inviting} style={{ flex: 2, padding: '0.5rem', borderRadius: 6, background: '#4f46e5', color: 'white', border: 'none', cursor: 'pointer', opacity: !inviteEmail.trim() || inviting ? 0.5 : 1 }}>
                  {inviting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Classes Section */}
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2 className="section-title">Classes</h2>
              <p className="section-description">
                Configure your class schedule. Disabled classes won't appear on the homework screen.
              </p>
            </div>
            <div className="section-actions">
              {!isEditingClasses ? (
                <button className="edit-btn" onClick={startEditingClasses}>
                  Edit
                </button>
              ) : (
                <>
                  <button
                    className="cancel-btn"
                    onClick={cancelEditingClasses}
                    disabled={isSavingClasses}
                  >
                    Cancel
                  </button>
                  <button
                    className="save-btn"
                    onClick={saveClasses}
                    disabled={isSavingClasses}
                  >
                    {isSavingClasses ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="classes-list">
            {draftClasses.map(cls => (
              <div key={cls.period} className={`class-item ${!cls.enabled ? 'class-item--disabled' : ''}`}>
                <div className="class-item-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={cls.enabled}
                      onChange={() => toggleClassEnabled(cls.period)}
                      disabled={!isEditingClasses}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="period-number">Period {cls.period}</span>
                </div>

                <div className="class-item-fields">
                  <input
                    type="text"
                    className="class-input class-input--subject"
                    value={cls.subject}
                    onChange={(e) => updateClassSubject(cls.period, e.target.value)}
                    placeholder="Subject"
                    disabled={!isEditingClasses || !cls.enabled}
                    readOnly={!isEditingClasses}
                  />
                  <input
                    type="text"
                    className="class-input class-input--teacher"
                    value={cls.teacher}
                    onChange={(e) => updateClassTeacher(cls.period, e.target.value)}
                    placeholder="Teacher"
                    disabled={!isEditingClasses || !cls.enabled}
                    readOnly={!isEditingClasses}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recurring Homework Section */}
        <div className="settings-section">
          <div className="section-header">
            <div>
              <h2 className="section-title">Recurring Homework</h2>
              <p className="section-description">
                Set up homework that repeats every day for specific classes.
              </p>
            </div>
            <div className="section-actions">
              {!isEditingRecurring ? (
                <button className="edit-btn" onClick={startEditingRecurring}>
                  Edit
                </button>
              ) : (
                <>
                  <button
                    className="cancel-btn"
                    onClick={cancelEditingRecurring}
                    disabled={isSavingRecurring}
                  >
                    Cancel
                  </button>
                  <button
                    className="save-btn"
                    onClick={saveRecurring}
                    disabled={isSavingRecurring}
                  >
                    {isSavingRecurring ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="recurring-items">
            {draftRecurring.map(item => (
              <div key={item.id} className="settings-recurring-item">
                <div className="recurring-item-header">
                  <span className="period-label">
                    Period {item.period}: {getClassDisplayName(item.period)}
                  </span>
                  {isEditingRecurring && (
                    <button
                      className="delete-btn"
                      onClick={() => deleteRecurring(item.id)}
                      title="Delete recurring homework"
                    >
                      ×
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  className="recurring-text-input"
                  value={item.text}
                  onChange={(e) => updateRecurringText(item.id, e.target.value)}
                  placeholder="Enter homework description..."
                  disabled={!isEditingRecurring}
                  readOnly={!isEditingRecurring}
                />

                <select
                  className="period-select"
                  value={item.period}
                  onChange={(e) => updateRecurringPeriod(item.id, parseInt(e.target.value))}
                  disabled={!isEditingRecurring}
                >
                  {enabledClasses.map(cls => (
                    <option key={cls.period} value={cls.period}>
                      Period {cls.period}: {cls.subject} - {cls.teacher}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            {draftRecurring.length === 0 && !isEditingRecurring && (
              <p className="no-items-message">No recurring homework configured.</p>
            )}
          </div>

          {isEditingRecurring && (
            <>
              <button
                className="add-recurring-btn"
                onClick={addRecurringItem}
                disabled={enabledClasses.length === 0}
              >
                + Add Recurring Homework
              </button>
              {enabledClasses.length === 0 && (
                <p className="no-classes-warning">Enable at least one class to add recurring homework.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen;
